import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, FolderOpen, BookOpen, Award, Plus, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import dashboardService from '../services/dashboard';
import AlertsWidget from '../components/dashboard/AlertsWidget';
import RecentProjectsWidget from '../components/dashboard/RecentProjectsWidget';
import OnboardingModal from '../components/OnboardingModal';

export default function Dashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const navigate = useNavigate();

  // Charger les données du dashboard une seule fois au montage
  useEffect(() => {
    loadDashboardData();
  }, []); // Dépendances vides = exécution unique au montage

  // Vérifier l'onboarding séparément quand user change
  useEffect(() => {
    if (user && user.firstLogin === true && user.subscriptionPlan === 'free') {
      console.log('User data:', { firstLogin: user.firstLogin, subscriptionPlan: user.subscriptionPlan });
      console.log('Affichage du modal onboarding');
      setShowOnboarding(true);
    }
  }, [user?.firstLogin, user?.subscriptionPlan]); // Dépendances spécifiques pour éviter la boucle

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getDashboardData();
      setDashboardData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertAction = (action) => {
    if (action.url) {
      navigate(action.url);
    }
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="mb-8">
          <div className="h-8 bg-gray-200 rounded w-64 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-100 rounded w-96 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card-static h-40 animate-pulse"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-static h-96 animate-pulse"></div>
          <div className="card-static h-96 animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-fade-in">
        <h1 className="text-2xl font-heading font-bold text-gray-900 mb-6">Tableau de bord</h1>
        <div className="bg-danger/5 border border-danger/20 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-danger mb-2">Erreur de chargement</h3>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button onClick={loadDashboardData} className="btn btn-outline">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const { statistics, recentProjects, alerts } = dashboardData || {};

  return (
    <div className="animate-fade-in">
      {/* Modal d'onboarding */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">Tableau de bord</h1>
        <p className="text-gray-600">Vue d'ensemble de vos projets et correspondances RNCP</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Projets actifs */}
        <div className="card-kpi group animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
              <FolderOpen className="w-6 h-6" />
            </div>
          </div>
          <div className="kpi-label">Projets actifs</div>
          <div className="kpi-value">{statistics?.activeProjects || 0}</div>
          <div className="mt-3 flex items-center gap-2">
            <span className="kpi-badge-positive">
              <TrendingUp className="w-3 h-3" />
              {statistics?.totalProjects || 0} au total
            </span>
          </div>
        </div>

        {/* Correspondances */}
        <div className="card-kpi group animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-secondary-50 text-secondary-600 group-hover:bg-secondary-100 transition-colors">
              <BarChart3 className="w-6 h-6" />
            </div>
          </div>
          <div className="kpi-label">Correspondances</div>
          <div className="kpi-value">{statistics?.totalCorrespondances || 0}</div>
          <div className="mt-3 flex items-center gap-2">
            <span className="kpi-badge-positive">
              <TrendingUp className="w-3 h-3" />
              Mappings actifs
            </span>
          </div>
        </div>

        {/* Compétences */}
        <div className="card-kpi group animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-accent-50 text-accent-600 group-hover:bg-accent-100 transition-colors">
              <BookOpen className="w-6 h-6" />
            </div>
          </div>
          <div className="kpi-label">Compétences</div>
          <div className="kpi-value">{statistics?.totalCompetences || 0}</div>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Référentiel global</span>
          </div>
        </div>

        {/* Certifications RNCP */}
        <div className="card-kpi group animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
              <Award className="w-6 h-6" />
            </div>
          </div>
          <div className="kpi-label">Certifications RNCP</div>
          <div className="kpi-value">{statistics?.totalRncpCertifications || 0}</div>
          <div className="mt-3">
            <div className="progress">
              <div className="progress-bar" style={{ width: '90%' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Alertes */}
        <div className="animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <AlertsWidget
            alerts={alerts || []}
            onActionClick={handleAlertAction}
          />
        </div>

        {/* Projets récents */}
        <div className="animate-slide-up" style={{ animationDelay: '0.5s' }}>
          <RecentProjectsWidget projects={recentProjects || []} />
        </div>
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-slide-up" style={{ animationDelay: '0.6s' }}>
        <button
          onClick={() => navigate('/projects')}
          className="card-static group p-6 flex items-center gap-4 hover:border-primary-300 transition-all"
        >
          <div className="p-3 rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all">
            <Plus className="w-6 h-6" />
          </div>
          <div className="text-left">
            <div className="font-semibold text-gray-900">Nouveau projet</div>
            <div className="text-sm text-gray-500">Créer un projet de correspondance</div>
          </div>
        </button>

        <button
          onClick={() => navigate('/rncp-visualization')}
          className="card-static group p-6 flex items-center gap-4 hover:border-accent-300 transition-all"
        >
          <div className="p-3 rounded-lg bg-accent-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-white transition-all">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div className="text-left">
            <div className="font-semibold text-gray-900">Visualisation</div>
            <div className="text-sm text-gray-500">Explorer les données RNCP</div>
          </div>
        </button>
      </div>
    </div>
  );
}
